import { useEffect, useRef } from 'react';
import { androidtvRefreshDevice, androidtvTouchFocus } from '../installed/androidtv';
import { samsungRefreshDevice, samsungTouchFocus } from '../installed/samsung';
import { lgRefreshDevice } from '../installed/air';
import { ensureAndroidTvInRoom, getAndroidTvByHabitacion, patchAndroidTvDevice } from '../installed/androidtv';
import { ensureSamsungInRoom, getSamsungByHabitacion, upsertSamsungDevice } from '../installed/samsung';
import { ensureLgInRoom, getLgByHabitacion, upsertLgDevice } from '../installed/air';
import { getSonosByHabitacion, refrescarSonosPorIds } from '../installed/music';

/** Mientras la habitación está abierta: volver a pedir estado de las tarjetas visibles. */
const ROOM_REFRESH_MS = 5_000;
/** Renueva foco backend (TTL ~45s) para poll agresivo Android / health Samsung TV. */
const FOCUS_RENEW_MS = 30_000;

/**
 * Al entrar a una habitación (y cada 5s mientras sigue abierta):
 * pide estado fresco de Android, Samsung (aire+TV), LG y Sonos de esa habitación.
 * Evo Home y Pileta no entran: ya tienen su propio canal.
 * Al salir se corta. El fondo periódico de cada integración sigue igual.
 */
export function useRoomAvSync(selectedRoomId: number | null | undefined): void {
  const roomIdRef = useRef(selectedRoomId);
  roomIdRef.current = selectedRoomId;

  useEffect(() => {
    if (!selectedRoomId) return;

    let cancelled = false;
    let inFlight = false;
    const roomId = selectedRoomId;
    // Ids vistos en esta visita. Si un tick las saca de la habitación, se siguen
    // pidiendo: el refresh del servidor las vuelve a poner.
    const pinnedAndroid = new Set<number>();
    const pinnedSamsung = new Set<number>();
    const pinnedLg = new Set<number>();
    const pinnedSonos = new Set<number>();

    const androidIds = () =>
      getAndroidTvByHabitacion(roomId)
        .filter((d) => d.paired && d.enabled !== false)
        .map((d) => d.id);

    const samsungIds = () =>
      getSamsungByHabitacion(roomId)
        .filter((d) => d.enabled !== false)
        .map((d) => d.id);

    const samsungTvIds = () =>
      getSamsungByHabitacion(roomId)
        .filter((d) => d.device_kind === 'tv' && d.enabled !== false)
        .map((d) => d.id);

    const lgIds = () =>
      getLgByHabitacion(roomId)
        .filter((d) => d.enabled !== false)
        .map((d) => d.id);

    const sonosIds = () =>
      getSonosByHabitacion(roomId)
        .filter((s) => s.enabled !== false)
        .map((s) => s.id);

    const refreshOnce = () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      androidIds().forEach((id) => pinnedAndroid.add(id));
      samsungIds().forEach((id) => pinnedSamsung.add(id));
      lgIds().forEach((id) => pinnedLg.add(id));
      sonosIds().forEach((id) => pinnedSonos.add(id));
      const atvIds = [...pinnedAndroid];
      const smIds = [...pinnedSamsung];
      const lIds = [...pinnedLg];
      const soIds = [...pinnedSonos];

      const tasks: Promise<unknown>[] = [
        ...atvIds.map((id) =>
          androidtvRefreshDevice(id)
            .then((d) => {
              if (cancelled) return;
              if (d) patchAndroidTvDevice(d);
              ensureAndroidTvInRoom(roomId, d?.id ? d : { id });
            })
            .catch(() => {
              if (!cancelled) ensureAndroidTvInRoom(roomId, { id });
            }),
        ),
        ...smIds.map((id) =>
          samsungRefreshDevice(id)
            .then((d) => {
              if (cancelled) return;
              if (d) upsertSamsungDevice(d);
              ensureSamsungInRoom(roomId, d?.id ? d : { id });
            })
            .catch(() => {
              if (!cancelled) ensureSamsungInRoom(roomId, { id });
            }),
        ),
        ...lIds.map((id) =>
          lgRefreshDevice(id)
            .then((d) => {
              if (cancelled) return;
              if (d) upsertLgDevice(d);
              ensureLgInRoom(roomId, d?.id ? d : { id });
            })
            .catch(() => {
              if (!cancelled) ensureLgInRoom(roomId, { id });
            }),
        ),
      ];
      if (soIds.length) {
        tasks.push(refrescarSonosPorIds(soIds).catch(() => undefined));
      }

      void Promise.all(tasks).finally(() => {
        inFlight = false;
      });
    };

    const renewFocus = () => {
      if (cancelled || roomIdRef.current !== roomId) return;
      const atv = androidIds();
      const tvs = samsungTvIds();
      if (atv.length) {
        void androidtvTouchFocus(atv).catch(() => undefined);
      }
      if (tvs.length) {
        void samsungTouchFocus(tvs).catch(() => undefined);
      }
    };

    refreshOnce();
    renewFocus();

    const refreshTimer = setInterval(refreshOnce, ROOM_REFRESH_MS);
    const focusTimer = setInterval(renewFocus, FOCUS_RENEW_MS);
    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
      clearInterval(focusTimer);
    };
  }, [selectedRoomId]);
}
